/**
 * VacantList — all vacant boxes across branches (field 38 = VACANT).
 *
 * Wires `/accounts/safedep-box-srch` with `BoxOccupType: 'UnOccup'` for each
 * configured branch. Placeholder dev data used until config seeds load.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { DataGrid, Pill, EntityIcon, Button } from '@/components/primitives';
import type { DataGridColumn } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import { RefreshCw } from 'lucide-react';
import type { SdbBoxRow } from './types';

export function VacantList(): JSX.Element {
  const apiClient = useApiClient();
  const { config } = useConfig();
  const navigate = useNavigate();
  const [rows, setRows] = useState<ReadonlyArray<SdbBoxRow>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    void (async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const branches = config.branches.length > 0
          ? config.branches.map((b) => b.branchCode)
          : ['1', '2', '4'];

        const results = await Promise.allSettled(
          branches.map((b) =>
            apiClient.accounts.safedepBoxSrch(
              { BranchCode: b, BoxOccupType: 'UnOccup' },
              { signal: ctrl.signal },
            ),
          ),
        );
        if (ctrl.signal.aborted) return;

        const merged: SdbBoxRow[] = [];
        results.forEach((r, idx) => {
          if (r.status !== 'fulfilled') return;
          const boxes = (r.value as unknown as {
            Boxes?: Array<{
              AcctId: string;
              BrCode?: string;
              ProdCode?: string;
              ProdDesc?: string;
              RentAmt?: string;
              AcctStat?: string;
            }>;
          }).Boxes ?? [];
          const br = branches[idx] ?? '';
          for (const b of boxes) {
            merged.push({
              acctId: b.AcctId,
              branchCode: b.BrCode ?? br,
              boxNumber: b.AcctId,
              boxSize: b.ProdDesc ?? b.ProdCode ?? '',
              rentAmount: Number.parseFloat(b.RentAmt ?? '0') || 0,
              nextDueDate: '',
              balance: 0,
              lateFee: 0,
              keyDeposit: 0,
              keysMissing: false,
              vacant: true,
            });
          }
        });
        setRows(merged);
      } catch (err) {
        if (ctrl.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load vacant boxes.');
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [apiClient, config.branches]);

  const columns: DataGridColumn<SdbBoxRow>[] = [
    {
      key: 'box',
      label: 'Box',
      render: (r) => (
        <span className="rsm-cell--acct">
          <EntityIcon type="safedeposit" code="SD" />
          <span>
            <strong>{r.acctId}</strong>
            <div className="rsm-cell--sub">{r.boxSize}</div>
          </span>
        </span>
      ),
    },
    { key: 'branch', label: 'Branch', render: (r) => r.branchCode },
    {
      key: 'rent',
      label: 'Annual rent',
      numeric: true,
      render: (r) =>
        r.rentAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
    },
    { key: 'status', label: 'Status', render: () => <Pill variant="ok">Vacant</Pill> },
  ];

  return (
    <div>
      {error && (
        <div className="rsm-banner rsm-banner--err" role="alert">
          {error}
        </div>
      )}
      {loading && (
        <div className="rsm-banner rsm-banner--info">
          <RefreshCw size={14} className="rsm-inline-icon" /> Loading vacant boxes…
        </div>
      )}
      <DataGrid<SdbBoxRow>
        columns={columns}
        rows={[...rows]}
        rowKey={(r) => `${r.branchCode}-${r.acctId}`}
        onRowAction={(r) => navigate(`box/${r.acctId}`)}
        emptyMessage="No vacant boxes available. Check with branch staff before quoting rentals."
      />
      <div style={{ marginTop: 'var(--sp-5)' }}>
        <Button
          variant="ghost"
          startIcon={<RefreshCw size={14} />}
          onClick={() => window.location.reload()}
        >
          Refresh
        </Button>
      </div>
    </div>
  );
}
